import type {
	AdditionalCategory,
	CoworkingExtra,
	CoworkingService,
	Equipment,
	FoodAndDrink,
	KeyAmenity,
	LandCategory,
	ResidentialParking,
	RoomAdjacency,
	WallsType,
} from '@rentflowdev/api'

/**
 * The closed answer lists shared by the property forms — one home for each.
 *
 * They are tuples so `z.enum` can be built straight from them, and they are
 * checked in both directions against the union `@rentflowdev/api` derives from
 * the Prisma enum: a value the enum does not have fails to compile, and so does
 * a list that leaves one of its members out.
 *
 * Six of these lists used to be written out in the step that renders them and
 * validated as `z.array(z.string())` in the schema, which is two copies with
 * nothing holding them together — a typo in either was a value the database
 * would reject with no sign of it in the browser.
 */
const closedList =
	<U extends string>() =>
	<const T extends readonly [U, ...U[]]>(
		values: T & ([U] extends [T[number]] ? unknown : never),
	): T =>
		values

// ── Coworking amenity sets ──────────────────────────────────────────────────
export const KEY_AMENITY_VALUES = closedList<KeyAmenity>()([
	'MODERN_RENOVATION', 'FURNITURE', 'WIFI', 'LEGAL_ADDRESS', 'SECURITY',
])

export const EQUIPMENT_VALUES = closedList<Equipment>()([
	'PRINTER', 'SCANNER', 'FAX', 'FLIPCHART',
])

export const FOOD_AND_DRINK_VALUES = closedList<FoodAndDrink>()([
	'TEA_COFFEE', 'SNACKS', 'KITCHEN', 'CAFE',
])

export const COWORKING_SERVICE_VALUES = closedList<CoworkingService>()([
	'TECH_SUPPORT', 'RECEPTION', 'CLEANING', 'SPECIAL_ZONES', 'LOCKERS',
	'PHONE_BOOTHS', 'MEETING_ROOMS',
])

export const COWORKING_EXTRA_VALUES = closedList<CoworkingExtra>()([
	'LOUNGE', 'EVENTS', 'TRANSFER', 'PARKING', 'BIKE_PARKING', 'GYM',
])

/**
 * Every category an ad may additionally be listed under. Which subset a form
 * offers depends on the property type — see `ADDITIONAL_CATEGORY_OPTIONS` in
 * `step-2-details.tsx` — but a stored value may come from any of them, so the
 * schema validates against the whole list.
 */
export const ADDITIONAL_CATEGORY_VALUES = closedList<AdditionalCategory>()([
	'FREE_PURPOSE', 'RETAIL', 'COWORKING', 'OFFICE', 'CATERING', 'BUILDING',
	'PRODUCTION', 'CAR_SERVICE', 'WAREHOUSE',
])

// ── Single-answer lists ─────────────────────────────────────────────────────
/** What a house is built of. `BuildingType` answers this for a block of flats. */
export const WALLS_TYPE_VALUES = closedList<WallsType>()([
	'BRICK', 'TIMBER', 'LOG', 'AERATED_BLOCK', 'METAL', 'FOAM_BLOCK',
	'SANDWICH_PANEL', 'RC_PANEL', 'EXPERIMENTAL',
])

/** How the rooms of a flat relate to one another, not how many there are. */
export const ROOM_ADJACENCY_VALUES = closedList<RoomAdjacency>()([
	'ADJACENT', 'ISOLATED',
])

/**
 * Parking at a home. Separate from the `parking` string list, which names a
 * building's facilities, and from `CommercialParking`, which never offers a
 * garage or a numbered space.
 */
export const RESIDENTIAL_PARKING_VALUES = closedList<ResidentialParking>()([
	'NONE', 'STREET', 'IN_BUILDING', 'GARAGE', 'PARKING_SPACE',
])

/** What a plot of land may legally be used for. */
export const LAND_CATEGORY_VALUES = closedList<LandCategory>()([
	'SETTLEMENTS', 'AGRICULTURAL', 'INDUSTRIAL',
])
